<template>
  <CustomTable ref="table" class="business-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:table>
      <thead>
        <tr>
          <custom-header class="col-fit" field="dueDate">Échéance</custom-header>
          <custom-header field="number" class="hide-for-mobile col-fit">Devis</custom-header>
          <custom-header field="invoiceNumber" class="hide-for-mobile col-fit">Documents</custom-header>
          <custom-header field="title" class="hide-for-mobile col-fill">Titre</custom-header>
          <custom-header field="status" class="hide-for-mobile col-fit">Status</custom-header>
          <custom-header class="col-fit" field="clientName">Client</custom-header>
          <custom-header field="ttcAmount" class="hide-for-mobile col-fit">Montant</custom-header>
          <custom-header class="col-fit" field="remaining">Solde</custom-header>
        </tr>
      </thead>
      <tbody v-if="!isLoading && data && data.length > 0">
        <tr v-for="(business, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" :class="{hide : isHiddenBusiness(business), 'business-row': !isHiddenBusiness(business)}" @click="openBusiness(business)">
          <template v-if="!refusedOrAbortedBusinessVisibility && (business.status === 'ABORTED' || business.status === 'REFUSED')">
            <td colspan="9" class="hide-for-mobile">Devis annulé ou refusé <a @click="this.refusedOrAbortedBusinessVisibility = !this.refusedOrAbortedBusinessVisibility">(afficher)</a></td>
          </template>
          <template v-else>
            <custom-body field="dueDate"
                         :title="business.invoiceCreationDate ? 'Facture générée le ' + new Date(business.invoiceCreationDate).toLocaleDateString() : ''"
                         class="only-desktop-table-cell col-fit"
                         :class="{ 'alert' : isDeadlinePassed(business.dueDate) }"
                         style="max-width: 90px">
              {{ business.dueDate ? calculateDeadlineDays(business.dueDate) : '-'}}
            </custom-body>
            <custom-body field="number" class="hide-for-mobile col-fit">
              <QuoteNumber :number="business.number" :order-number="haveProductionOption ? business.orderNumber : undefined" />
            </custom-body>
            <custom-body field="invoiceNumber" class="hide-for-mobile col-fit">
              <DocumentChips :invoice-number="business.invoiceNumber" :deposit-number="business.depositNumber" :credit-note-numbers="(business.creditNotes || []).map(creditNote => creditNote.number)" />
            </custom-body>
            <custom-body field="title" class="hide-for-mobile col-fill" :title="business.title">{{ business.title ? business.title : ''}}</custom-body>
            <custom-body field="status" class="hide-for-mobile col-fit"><StatusBadge :status="business.status" /></custom-body>
            <custom-body class="col-fit cell-strong col-truncate" field="clientName" :title="business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '')">{{ business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '')}}</custom-body>
            <custom-body field="ttcAmount" class="hide-for-mobile col-fit cell-strong">{{ business.ttcAmount ? business.ttcAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : ''}}</custom-body>
            <custom-body class="col-fit" field="remaining">{{business.remaining && business.invoiceNumber ? business.remaining.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })  + ' €' : ''}}</custom-body>
          </template>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row">
        <td colspan="8" class="hide-for-mobile">Aucun contrat enregistré</td>
      </tr></tbody>
    </template>
  </CustomTable>
  <BusinessManagementModal ref="businessManagementModal" :callback=loadData />
</template>

<script>
import {BusinessController} from "@/controllers/business/BusinessController";
import BusinessManagementModal from "@/components/modals/BusinessManagementModal.vue";
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import CustomTable from "@/components/CustomTable.vue";
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMoneyBillTransfer, faList, faChevronLeft, faChevronRight, faPlus, faFilePen, faFileLines, faFilePdf, faCircleInfo, faDownload } from "@fortawesome/free-solid-svg-icons";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import DocumentChips from "@/components/tables/commons/DocumentChips.vue";
import QuoteNumber from "@/components/tables/commons/QuoteNumber.vue";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {COUNTS_REFRESH_EVENT} from "@/components/businessTabs";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
library.add(faChevronLeft, faPlus, faChevronRight, faMoneyBillTransfer, faList, faFilePen, faFileLines, faFilePdf, faCircleInfo, faDownload);

export default {
  name: 'UnpaidTable',
  computed: {
    BUSINESS_STATUS_ENUM() {
      return BUSINESS_STATUS_ENUM
    },
    haveProductionOption() {
      return localStorage && localStorage.options && localStorage.options.includes('production');
    }
  },
  components: {
    CustomBody,
    StatusBadge,
    DocumentChips,
    QuoteNumber,
    CustomHeader,
    CustomTable,
    BusinessManagementModal,
  },
  data: function () {
    return {
      data: [],
      elements: [
        {label: "Échéance", value: "dueDate", type: "date", sortOnly: true},
        {label: "Date de facture", value: "invoiceCreationDate", type: "date"},
        {label: "N°Devis", value: "number", type: "number"},
        {label: "N°Commande", value: "orderNumber", type: "number"},
        {label: "N°Facture", value: "invoiceNumber", type: "number"},
        {label: "N°Avoir", value: "creditNoteNumber", type: "number"},
        {label: "Titre", value: "title", type: "text"},
        {label: "Status", value: "status", type: "select", values: BUSINESS_STATUS_ENUM},
        {label: "Client", value: "clientName", type: "text"},
        {label: "Montant", value: "ttcAmount", type: "number"},
        {label: "Solde", value: "remaining", type: "number"},
        {label: "Actions", value: undefined, type: undefined}
      ],
      searchOptions: {
        filters: [
          {field: "status", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [BUSINESS_STATUS_ENUM.REFUSED.value, BUSINESS_STATUS_ENUM.ABORTED.value]},
          {field: "status", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [BUSINESS_STATUS_ENUM.PAID.value, BUSINESS_STATUS_ENUM.PAID_AND_DECLARED.value]}
        ],
        sorts: [
          {field: "number", order: SORT_OPERATOR_ENUM.DESCENDING.value}
        ]
      },
      isLoading: true,
      hasLoadedOnce: false,
      refusedOrAbortedBusinessVisibility: false
    }
  },
  beforeMount() {
    this.searchOptions = {
      filters: [
        {field: "status", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [BUSINESS_STATUS_ENUM.REFUSED.value, BUSINESS_STATUS_ENUM.ABORTED.value]},
        {field: "invoiceNumber", operator: FILTER_OPERATOR_ENUM.GREATER_THAN.value, variable: 0}
      ],
      sorts: [
        {field: "dueDate", order: SORT_OPERATOR_ENUM.ASCENDING.value}
      ]
    };
  },
  async mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();

    this.$refs.table.$refs.paginationArray.paginationInputs.pages.readOnly = true;
    this.$refs.table.$refs.paginationArray.isNextReloadCancel = true;
    this.$refs.table.$refs.paginationArray.paginationInputs.pages.value = undefined;
    this.$refs.table.$refs.paginationArray.index = undefined;

    await this.loadData();

    // Clean URL without reload
    const url = new URL(window.location.href)
    const baseUrl = url.origin + url.pathname
    window.history.replaceState({}, '', baseUrl)
  },
  methods: {
    isWaitingDeposit(business) {
      const depositRemaining = business.orderId && business.deposit && business.depositRemaining;
      const quoteSent = business && [
        BUSINESS_STATUS_ENUM.SENT.value,
        BUSINESS_STATUS_ENUM.ACCEPTED.value,
        BUSINESS_STATUS_ENUM.IN_PROGRESS.value,
        BUSINESS_STATUS_ENUM.DONE.value
      ].includes(business.status);
      const isInvoice = !!business.invoiceNumber;
      return (quoteSent || isInvoice) && depositRemaining;
    },
    isWaitingSignature(business) {
      return business && business.status === BUSINESS_STATUS_ENUM.SENT.value && business.signature && !business.signature.signedAt;
    },
    // L'échéance (date de facture + délai de paiement, 0 = « à la réception ») est calculée par le back
    // Une facture est en retard une fois la journée d'échéance terminée
    isDeadlinePassed(dueDate) {
      if (!dueDate) {
        return false
      }

      const deadline = new Date(dueDate)
      deadline.setHours(23, 59, 59, 999)

      return Date.now() > deadline.getTime()
    },
    calculateDeadlineDays(dueDate){
      const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const days = Math.round((startOfDay(new Date(dueDate)) - startOfDay(new Date())) / (24 * 60 * 60 * 1000));
      if (days === 0) return "Aujourd'hui";
      const unit = Math.abs(days) > 1 ? 'jours' : 'jour';
      return days > 0 ? `Dans ${days} ${unit}` : `Il y a ${-days} ${unit}`;
    },
    async loadData(){
      this.$refs.table?.startLoading();
      this.isLoading = true;
      this.data = [];

      let resp = await BusinessController.getAllFilteredByUnpaidCollections(this.$refs.table.buildURLParams());

      if(resp && !resp.errors){
        this.data.push(...resp);
      }

      this.isLoading = false;
      this.$refs.table?.stopLoading();
      // Les compteurs du menu ne sont recalculés que si les données ont changé (pas à l'ouverture de la rubrique)
      if (this.hasLoadedOnce) window.dispatchEvent(new Event(COUNTS_REFRESH_EVENT));
      this.hasLoadedOnce = true;
    },
    // Après création d'un devis : la modale du nouveau devis s'ouvre sans attendre le rechargement du tableau
    async openCreatedBusiness(businessId) {
      this.loadData();
      const business = await BusinessController.get(businessId);
      if (business && !business.errors) {
        this.$refs.businessManagementModal.openModal(business);
      }
    },
    isHiddenBusiness(business) {
      return !this.refusedOrAbortedBusinessVisibility && (business.status === 'ABORTED' || business.status === 'REFUSED');
    },
    // Le tableau ne reçoit que les champs affichés : la modale a besoin du business complet
    async openBusiness(business) {
      if (this.isHiddenBusiness(business) || !business.id) return;
      const fullBusiness = await BusinessController.get(business.id);
      if (fullBusiness && !fullBusiness.errors) {
        // Les impayés sont des factures : la modale s'ouvre directement sur la facture
        this.$refs.businessManagementModal.openModal(fullBusiness, {type: BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value, creditNoteNumber: null});
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl';
  @require '~/src/common/style/businessTable.styl';
  .alert
    color #dc0000
    font-weight bold
  .classic
    color auto
  .warning
    color #d58900
    font-weight bold

  .fa-file-pdf
    margin-top -2px
    height 27px !important
    width 27px !important

  .button-icon
    margin-right 10px

  .hide
    height 25px
    td
      text-align center
      a
        cursor pointer

  .action-td
    overflow visible
    display flex
    align-items center
    height 30px

  .tooltip-container
    width 40px
    margin-left 5px
    position relative

    .tooltip-icon
      width 25px
      height 25px
      color INFO
      &:hover + .tooltip-message
        visibility visible
        opacity 1

    .tooltip-message
      display flex
      flex-direction column
      background-color white
      position absolute
      width 260px
      visibility hidden
      opacity 0
      top -6px
      left -280px
      z-index 10
      box-shadow 0 0 5px 0 rgba(0,0,0,.3)
      border-radius 5px
      padding 5px
      transition visibility 0s, opacity .3s linear

    .tooltip-message:hover
      visibility visible
      opacity 1
</style>
